import React, { memo, useEffect, useMemo } from 'react';

import cn from 'classnames';

import { useFileItem } from '@entities/attachment/lib/hooks';
import { getNameExtension, isImg } from '@shared/helpers';
import { CircularLoader, Icon, Image, UiButton } from '@shared/ui';

import cls from './FileItemRaw.module.css';
import { FileItemSvg } from '../FileItem/FileItemSvg';

interface IFileItem {
    file: File;
    isLoading: boolean;
    fileItemClassName?: string;
    handleClick?: () => void;
    hasFileInfo?: boolean;
    onCancel?: (file: File) => void;
    cancelButtonClassName?: string;
    cancelButtonTitle?: string;
    renderCancelAction?: (params: {
        file: File;
        onCancel: () => void;
        className?: string;
    }) => React.ReactNode;
}

export const FileItemRaw = memo(
    ({
        file,
        handleClick,
        isLoading,
        fileItemClassName,
        hasFileInfo = false,
        onCancel,
        cancelButtonClassName,
        cancelButtonTitle = 'Удалить файл',
        renderCancelAction,
    }: IFileItem) => {
        const { onFileItemClick } = useFileItem();
        const isImage = isImg(file.type);
        const imgUrl = useMemo(() => (isImage ? URL.createObjectURL(file) : null), [file, isImage]);
        const fileName = file.name;
        const fileExtension = getNameExtension(file.name).extension;

        useEffect(
            () => () => {
                if (imgUrl) {
                    URL.revokeObjectURL(imgUrl);
                }
            },
            [imgUrl]
        );

        const handleCancelClick = () => onCancel?.(file);

        return (
            <li
                className={cls.FileItemWrapper}
                onClick={onFileItemClick}
                draggable={false}
                title={fileName}
            >
                <div
                    className={cn(cls.FileItem, fileItemClassName)}
                    onClick={() => handleClick?.()}
                >
                    {isLoading && <CircularLoader size={26} />}
                    {!isLoading && isImage && (
                        <Image
                            src={imgUrl}
                            width={46}
                            height={46}
                            draggable={false}
                            alt={fileName}
                            wrapperClassName={cls.ImgWrapper}
                            className={cls.AttachmentImg}
                        />
                    )}
                    {!isLoading && !isImage && (
                        <FileItemSvg extension={fileExtension} iconHeight={46} iconWidth={46} />
                    )}

                    {hasFileInfo && (
                        <div className={cls.FileInfoWrapper}>
                            {fileName && <p className={cls.fileName}>{fileName}</p>}
                            {fileExtension && (
                                <span className={cls.fileExtension}>.{fileExtension}</span>
                            )}
                        </div>
                    )}
                </div>

                {onCancel &&
                    (renderCancelAction ? (
                        renderCancelAction({
                            file,
                            onCancel: handleCancelClick,
                            className: cancelButtonClassName,
                        })
                    ) : (
                        <UiButton
                            className={cls.CancelBtn}
                            variant="icon"
                            title={cancelButtonTitle}
                            startIcon={
                                <Icon iconName="cross" width={10} height={10} fill="black" />
                            }
                            onClick={(event) => {
                                event.stopPropagation();
                                handleCancelClick();
                            }}
                        />
                    ))}
            </li>
        );
    }
);
